import {Card, Spinner} from 'react-bootstrap'
import {useEffect, useState} from 'react'
import ApexCharts, {ApexOptions} from 'apexcharts'
import {useLazyQuery} from '@apollo/client'
import {FormattedMessage, useIntl} from 'react-intl'
import {GET_POLICY_TYPE_TRENDS} from '../../../gql/queries/reportsQuery'
import {formatPeriodLabel} from './chartLocale'

interface IPolicyTypeTrendData {
  period: string
  periodLabel: string
  policyTypeName: string
  policiesCount: number
}

interface IResPolicyTypeTrends {
  policyTypeTrends: IPolicyTypeTrendData[]
}

interface IPolicyTypeTrendsChartProps {
  dateFrom: string
  dateTo: string
  dateCriteria: 'submitted' | 'effective'
  carrier?: string
  stateEu?: string
  policyType?: string
  status?: string[]
  agent?: string[]
  paymentStatus?: string[]
  advisorPaid?: boolean
  loading?: boolean
  refreshTrigger?: number
}

export const PolicyTypeTrendsChart = ({
  dateFrom,
  dateTo,
  dateCriteria,
  carrier,
  stateEu,
  policyType,
  status,
  agent,
  paymentStatus,
  advisorPaid,
  loading: parentLoading,
  refreshTrigger,
}: IPolicyTypeTrendsChartProps) => {
  const intl = useIntl()
  const [getTrendsData, {data, loading}] = useLazyQuery<IResPolicyTypeTrends>(
    GET_POLICY_TYPE_TRENDS,
    {
      fetchPolicy: 'no-cache',
    }
  )

  useEffect(() => {
    getTrendsData({
      variables: {
        dateFrom,
        dateTo,
        dateCriteria,
        carrier: carrier ? [carrier] : undefined,
        stateEu: stateEu ? [stateEu] : undefined,
        policyType: policyType ? [policyType] : undefined,
        status: status,
        agent: agent,
        paymentStatus: paymentStatus,
        advisorPaid: advisorPaid,
      },
    })
  }, [dateFrom, dateTo, dateCriteria, carrier, stateEu, policyType, status, agent, paymentStatus, advisorPaid, refreshTrigger, getTrendsData])

  useEffect(() => {
    if (!data?.policyTypeTrends || loading || parentLoading) return

    const trendsData = data.policyTypeTrends

    // Agrupar por tipo de póliza y mantener el orden cronológico
    const policyTypeMap = new Map<string, Map<string, number>>()
    const periodMap = new Map<string, string>() // period -> periodLabel

    trendsData.forEach((item) => {
      if (!policyTypeMap.has(item.policyTypeName)) {
        policyTypeMap.set(item.policyTypeName, new Map())
      }
      policyTypeMap.get(item.policyTypeName)!.set(item.period, item.policiesCount)
      periodMap.set(item.period, item.periodLabel)
    })

    // Ordenar períodos cronológicamente usando el campo 'period' (YYYY-MM o YYYY-MM-DD)
    const sortedPeriods = Array.from(periodMap.keys()).sort()
    const sortedLabels = sortedPeriods.map((period) => formatPeriodLabel(period, intl.locale))

    // Crear series para ApexCharts
    const series = Array.from(policyTypeMap.entries()).map(([policyTypeName, dataMap]) => ({
      name: policyTypeName,
      data: sortedPeriods.map((period) => dataMap.get(period) || 0),
    }))

    const options: ApexOptions = {
      series,
      chart: {
        type: 'line',
        height: 350,
        toolbar: {
          show: false,
        },
      },
      stroke: {
        curve: 'smooth',
        width: 3,
      },
      xaxis: {
        categories: sortedLabels,
        labels: {
          style: {
            fontSize: '12px',
          },
        },
      },
      yaxis: {
        logarithmic: true,
        labels: {
          style: {
            fontSize: '12px',
          },
          formatter: (val) => Math.floor(val).toString(),
        },
      },
      dataLabels: {
        enabled: false,
      },
      legend: {
        position: 'top',
        horizontalAlign: 'center',
        fontSize: '12px',
        markers: {
          width: 8,
          height: 8,
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: (val) =>
            intl.formatMessage(
              {
                id: 'dashboard.policies.tooltip.count',
                defaultMessage: '{count, plural, one {# policy} other {# policies}}',
              },
              {count: val}
            ),
        },
      },
      colors: [
        '#3b82f6',
        '#ef4444',
        '#10b981',
        '#f59e0b',
        '#8b5cf6',
        '#ec4899',
        '#14b8a6',
        '#f97316',
      ],
    }

    const chart = new ApexCharts(document.querySelector('#policy-type-trends-chart'), options)
    chart.render()

    return () => {
      chart.destroy()
    }
  }, [data, loading, parentLoading, intl])

  const getTitle = () => {
    return intl.formatMessage(
      {
        id: 'dashboard.policies.trendsChart.title',
        defaultMessage:
          'Policies {criteria, select, submitted {submitted} other {effective}} each month by product type',
      },
      {criteria: dateCriteria}
    )
  }

  const getSubtitle = () => {
    return (
      <>
        <FormattedMessage
          id='dashboard.policies.trendsChart.subtitle'
          defaultMessage='Number of policies for each product type grouped by month'
        />
        <span className='badge badge-light-warning ms-2'>
          <FormattedMessage
            id='dashboard.policies.trendsChart.logScale'
            defaultMessage='Logarithmic Scale'
          />
        </span>
      </>
    )
  }

  return (
    <Card className='card-flush h-100'>
      <Card.Header className='border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>{getTitle()}</span>
          <span className='text-muted fw-semibold fs-7'>{getSubtitle()}</span>
        </h3>
      </Card.Header>
      <Card.Body className='pt-0'>
        {(loading || parentLoading) && (
          <div className='d-flex justify-content-center align-items-center' style={{minHeight: 350}}>
            <Spinner animation='border' />
          </div>
        )}
        {!loading && !parentLoading && (
          <div id='policy-type-trends-chart' style={{minHeight: 350}}></div>
        )}
      </Card.Body>
    </Card>
  )
}
